import { useState, type ReactNode } from 'react';
import { Tabs } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { DoubleCheckModal, QuietAddTrigger } from '@shared/ui';
import {
    useColors,
    useFlowerTypes,
    useAssemblyTypes,
    useDeleteColor,
    useDeleteFlowerType,
    useDeleteAssemblyType,
    type Color,
    type FlowerType,
    type AssemblyType,
} from '@modules/products';
import { Breadcrumbs, OptionsTable } from '@components';
import EditColorModal from './components/EditColorModal';
import EditFlowerTypeModal from './components/EditFlowerTypeModal';
import EditAssemblyTypeModal from './components/EditAssemblyTypeModal';
import '@shared/styles/page.css';
import './OptionsPage.css';

function OptionsCard({
    title,
    addLabel,
    onAdd,
    children,
}: {
    title: string;
    addLabel: string;
    onAdd: () => void;
    children: ReactNode;
}) {
    return (
        <section className="options-card">
            <div className="options-card__head">
                <h2 className="options-card__title">{title}</h2>
                <QuietAddTrigger onClick={onAdd}>{addLabel}</QuietAddTrigger>
            </div>
            {children}
        </section>
    );
}

export default function OptionsPage() {
    const { data: colors = [], isLoading: colorsLoading, error: colorsError, refetch: refetchColors } = useColors();
    const { data: flowerTypes = [], isLoading: flowerTypesLoading, error: flowerTypesError, refetch: refetchFlowerTypes } = useFlowerTypes();
    const { data: assemblyTypes = [], isLoading: assemblyTypesLoading, error: assemblyTypesError, refetch: refetchAssemblyTypes } = useAssemblyTypes();

    const deleteColor = useDeleteColor();
    const deleteFlowerType = useDeleteFlowerType();
    const deleteAssemblyType = useDeleteAssemblyType();

    const [editingColor, setEditingColor] = useState<Color | null>(null);
    const [editingFlowerType, setEditingFlowerType] = useState<FlowerType | null>(null);
    const [editingAssemblyType, setEditingAssemblyType] = useState<AssemblyType | null>(null);
    const [activeTab, setActiveTab] = useState<string>('colors');
    const [isColorModalOpen, setIsColorModalOpen] = useState(false);
    const [isFlowerTypeModalOpen, setIsFlowerTypeModalOpen] = useState(false);
    const [isAssemblyTypeModalOpen, setIsAssemblyTypeModalOpen] = useState(false);
    const [isDeleteColorModalOpen, setIsDeleteColorModalOpen] = useState(false);
    const [isDeleteFlowerTypeModalOpen, setIsDeleteFlowerTypeModalOpen] = useState(false);
    const [isDeleteAssemblyTypeModalOpen, setIsDeleteAssemblyTypeModalOpen] = useState(false);
    const [colorToDelete, setColorToDelete] = useState<Color | null>(null);
    const [flowerTypeToDelete, setFlowerTypeToDelete] = useState<FlowerType | null>(null);
    const [assemblyTypeToDelete, setAssemblyTypeToDelete] = useState<AssemblyType | null>(null);

    const handleDeleteColor = (color: Color) => {
        setColorToDelete(color);
        setIsDeleteColorModalOpen(true);
    };

    const confirmDeleteColor = async () => {
        if (!colorToDelete) return;

        try {
            await deleteColor.mutateAsync(colorToDelete.id);
            notifications.show({
                title: 'Успешно',
                message: 'Цвет удален',
                color: 'green',
            });
            setIsDeleteColorModalOpen(false);
            setColorToDelete(null);
        } catch (error) {
            console.error('Ошибка при удалении цвета:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении цвета',
                color: 'red',
            });
        }
    };

    const cancelDeleteColor = () => {
        setIsDeleteColorModalOpen(false);
        setColorToDelete(null);
    };

    const handleDeleteFlowerType = (flowerType: FlowerType) => {
        setFlowerTypeToDelete(flowerType);
        setIsDeleteFlowerTypeModalOpen(true);
    };

    const confirmDeleteFlowerType = async () => {
        if (!flowerTypeToDelete) return;

        try {
            await deleteFlowerType.mutateAsync(flowerTypeToDelete.id);
            notifications.show({
                title: 'Успешно',
                message: 'Тип цветка удален',
                color: 'green',
            });
            setIsDeleteFlowerTypeModalOpen(false);
            setFlowerTypeToDelete(null);
        } catch (error) {
            console.error('Ошибка при удалении типа цветка:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении типа цветка',
                color: 'red',
            });
        }
    };

    const cancelDeleteFlowerType = () => {
        setIsDeleteFlowerTypeModalOpen(false);
        setFlowerTypeToDelete(null);
    };

    const handleDeleteAssemblyType = (assemblyType: AssemblyType) => {
        setAssemblyTypeToDelete(assemblyType);
        setIsDeleteAssemblyTypeModalOpen(true);
    };

    const confirmDeleteAssemblyType = async () => {
        if (!assemblyTypeToDelete) return;

        try {
            await deleteAssemblyType.mutateAsync(assemblyTypeToDelete.id);
            notifications.show({
                title: 'Успешно',
                message: 'Тип сборки удален',
                color: 'green',
            });
            setIsDeleteAssemblyTypeModalOpen(false);
            setAssemblyTypeToDelete(null);
        } catch (error) {
            console.error('Ошибка при удалении типа сборки:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении типа сборки',
                color: 'red',
            });
        }
    };

    const cancelDeleteAssemblyType = () => {
        setIsDeleteAssemblyTypeModalOpen(false);
        setAssemblyTypeToDelete(null);
    };

    return (
        <div className="page-container options-page">
            <Breadcrumbs items={[
                { label: 'Главная', path: '/' },
                { label: 'Справочники' }
            ]} />

            <div className="options-page-head">
                <h1 className="options-page-title">Справочники</h1>
                <p className="options-page-subtitle">
                    Цвета, типы цветов и типы сборки для карточек товаров
                </p>
            </div>

            <div className="options-page__scroll">
                <div className="content-wrapper options-page__content-wrapper">
                    <div className="options-page__content">
                    <Tabs
                        value={activeTab}
                        onChange={(value) => setActiveTab(value || 'colors')}
                        className="options-page__tabs options-tabs"
                    >
                        <Tabs.List className="options-page__tabs-list options-tabs-list">
                            <Tabs.Tab value="colors" className="options-tabs-tab">Цвета</Tabs.Tab>
                            <Tabs.Tab value="flowerTypes" className="options-tabs-tab">Типы цветов</Tabs.Tab>
                            <Tabs.Tab value="assemblyTypes" className="options-tabs-tab">Типы сборки</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="colors" className="options-page__tabs-panel options-tabs-panel">
                            <OptionsCard
                                title="Цвета"
                                addLabel="Добавить цвет"
                                onAdd={() => {
                                    setEditingColor(null);
                                    setIsColorModalOpen(true);
                                }}
                            >
                            <OptionsTable
                                columns={[
                                    { key: 'id', label: 'ID' },
                                    { key: 'name', label: 'Название' },
                                    {
                                        key: 'hexCode',
                                        label: 'HEX код',
                                        render: (color: Color) => color.hexCode || '-',
                                    },
                                    {
                                        key: 'color',
                                        label: 'Цвет',
                                        render: (color: Color) =>
                                            color.hexCode ? (
                                                <div
                                                    className="color-preview"
                                                    style={{ backgroundColor: color.hexCode }}
                                                />
                                            ) : null,
                                    },
                                ]}
                                data={colors}
                                loading={colorsLoading}
                                error={colorsError}
                                refetch={refetchColors}
                                getActions={(color: Color) => [
                                    {
                                        label: 'Редактировать',
                                        onClick: () => {
                                            setEditingColor(color);
                                            setIsColorModalOpen(true);
                                        },
                                    },
                                    {
                                        label: 'Удалить',
                                        onClick: () => handleDeleteColor(color),
                                        isDanger: true,
                                    },
                                ]}
                            />
                            </OptionsCard>
                        </Tabs.Panel>

                        <Tabs.Panel value="flowerTypes" className="options-page__tabs-panel options-tabs-panel">
                            <OptionsCard
                                title="Типы цветов"
                                addLabel="Добавить тип цветка"
                                onAdd={() => {
                                    setEditingFlowerType(null);
                                    setIsFlowerTypeModalOpen(true);
                                }}
                            >
                            <OptionsTable
                                columns={[
                                    { key: 'id', label: 'ID' },
                                    { key: 'name', label: 'Название' },
                                ]}
                                data={flowerTypes}
                                loading={flowerTypesLoading}
                                error={flowerTypesError}
                                refetch={refetchFlowerTypes}
                                getActions={(type: FlowerType) => [
                                    {
                                        label: 'Редактировать',
                                        onClick: () => {
                                            setEditingFlowerType(type);
                                            setIsFlowerTypeModalOpen(true);
                                        },
                                    },
                                    {
                                        label: 'Удалить',
                                        onClick: () => handleDeleteFlowerType(type),
                                        isDanger: true,
                                    },
                                ]}
                            />
                            </OptionsCard>
                        </Tabs.Panel>

                        <Tabs.Panel value="assemblyTypes" className="options-page__tabs-panel options-tabs-panel">
                            <OptionsCard
                                title="Типы сборки"
                                addLabel="Добавить тип сборки"
                                onAdd={() => {
                                    setEditingAssemblyType(null);
                                    setIsAssemblyTypeModalOpen(true);
                                }}
                            >
                            <OptionsTable
                                columns={[
                                    { key: 'id', label: 'ID' },
                                    { key: 'name', label: 'Название' },
                                ]}
                                data={assemblyTypes}
                                loading={assemblyTypesLoading}
                                error={assemblyTypesError}
                                refetch={refetchAssemblyTypes}
                                getActions={(type: AssemblyType) => [
                                    {
                                        label: 'Редактировать',
                                        onClick: () => {
                                            setEditingAssemblyType(type);
                                            setIsAssemblyTypeModalOpen(true);
                                        },
                                    },
                                    {
                                        label: 'Удалить',
                                        onClick: () => handleDeleteAssemblyType(type),
                                        isDanger: true,
                                    },
                                ]}
                            />
                            </OptionsCard>
                        </Tabs.Panel>
                    </Tabs>
                    </div>
                </div>
            </div>

            <EditColorModal
                isOpen={isColorModalOpen}
                onClose={() => {
                    setIsColorModalOpen(false);
                    setEditingColor(null);
                }}
                color={editingColor}
            />

            <EditFlowerTypeModal
                isOpen={isFlowerTypeModalOpen}
                onClose={() => {
                    setIsFlowerTypeModalOpen(false);
                    setEditingFlowerType(null);
                }}
                flowerType={editingFlowerType}
            />

            <EditAssemblyTypeModal
                isOpen={isAssemblyTypeModalOpen}
                onClose={() => {
                    setIsAssemblyTypeModalOpen(false);
                    setEditingAssemblyType(null);
                }}
                assemblyType={editingAssemblyType}
            />

            <DoubleCheckModal
                isOpen={isDeleteColorModalOpen}
                onClose={cancelDeleteColor}
                title="Удалить цвет?"
                message={colorToDelete ? `Вы уверены, что хотите удалить цвет "${colorToDelete.name}"?` : ''}
                secondButtonText="Удалить"
                secondButtonAction={confirmDeleteColor}
                isLoading={deleteColor.isPending}
            />

            <DoubleCheckModal
                isOpen={isDeleteFlowerTypeModalOpen}
                onClose={cancelDeleteFlowerType}
                title="Удалить тип цветка?"
                message={flowerTypeToDelete ? `Вы уверены, что хотите удалить тип цветка "${flowerTypeToDelete.name}"?` : ''}
                secondButtonText="Удалить"
                secondButtonAction={confirmDeleteFlowerType}
                isLoading={deleteFlowerType.isPending}
            />

            <DoubleCheckModal
                isOpen={isDeleteAssemblyTypeModalOpen}
                onClose={cancelDeleteAssemblyType}
                title="Удалить тип сборки?"
                message={assemblyTypeToDelete ? `Вы уверены, что хотите удалить тип сборки "${assemblyTypeToDelete.name}"?` : ''}
                secondButtonText="Удалить"
                secondButtonAction={confirmDeleteAssemblyType}
                isLoading={deleteAssemblyType.isPending}
            />
        </div>
    );
}

